import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { clientsApi } from '../api/clients'
import type { ChatMode } from '../api/chat'
import { ChatPanel } from '../components/ChatPanel'

export default function ChatClient() {
  const [params, setParams] = useSearchParams()
  const { data: clientsData } = useQuery({ queryKey: ['clients'], queryFn: () => clientsApi.list() })
  const clients = useMemo(
    () => (clientsData?.clients ?? []).slice().sort((a, b) => a.name.localeCompare(b.name)),
    [clientsData]
  )

  // ?client=<id>&mode=interview (links da página de Inteligência e do cadastro).
  const [clientId, setClientId] = useState(params.get('client') ?? '')
  const [mode, setMode] = useState<ChatMode>(params.get('mode') === 'interview' ? 'interview' : 'chat')
  // Muda = conversa nova (com mode=interview, a IA já abre a entrevista).
  const [startToken, setStartToken] = useState(params.get('mode') === 'interview' ? 1 : 0)
  // ?conversation=<id> (aba "Entrevistas" do cliente): abre essa conversa em vez da mais recente.
  const [openId, setOpenId] = useState<string | null>(params.get('conversation'))

  // Seleciona o primeiro cliente quando a lista carrega.
  useEffect(() => {
    if (!clientId && clients.length) setClientId(clients[0].id)
  }, [clients, clientId])

  // Os parâmetros da URL só valem na chegada.
  useEffect(() => {
    if (params.has('client') || params.has('mode')) setParams({}, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const selectedClient = clients.find((c) => c.id === clientId)

  function start(m: ChatMode) {
    setOpenId(null)
    setMode(m)
    setStartToken((n) => n + 1)
  }

  return (
    <div className="flex h-[calc(100vh-8rem)] flex-col space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-slate-900">Falar sobre</h1>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={clientId}
            onChange={(e) => {
              setClientId(e.target.value)
              setOpenId(null)
              setMode('chat')
              setStartToken(0)
            }}
            className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
          >
            {clients.length === 0 && <option value="">Nenhum cliente disponível</option>}
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => start('chat')}
            disabled={!clientId}
            className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            Nova conversa
          </button>
          <button
            type="button"
            onClick={() => start('interview')}
            disabled={!clientId}
            title="A IA faz perguntas curtas (~10 min) e salva no dossiê do cliente o que você responder."
            className="rounded-md bg-violet-600 px-3 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
          >
            🎤 Entrevista do dossiê
          </button>
        </div>
      </div>

      {clientId && (
        <ChatPanel
          clientId={clientId}
          newMode={mode}
          startToken={startToken}
          openConversationId={openId ?? undefined}
          className="flex-1"
          exportTitle={`Falar sobre${selectedClient ? ` — ${selectedClient.name}` : ''}`}
          exportSubtitle="Conversa com a IA sobre os relatórios e KPIs do cliente"
          emptyHint={
            <>
              Faça perguntas sobre os <strong>relatórios</strong> e os <strong>KPIs</strong>
              {selectedClient ? ` de ${selectedClient.name}` : ''}. A IA consulta os dados do cliente
              para responder — e guarda no dossiê o que você contar sobre o negócio. Ex.: "Como evoluiu o
              ROAS nas últimas semanas?". Para ensinar a IA de uma vez, use a{' '}
              <strong>🎤 Entrevista do dossiê</strong>.
            </>
          }
        />
      )}
    </div>
  )
}
